import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { IAppReduxStore } from '../../../redux/store';
import { vars } from '../../../Theme';
import { TooltipPlacement } from '../AppTooltip/AppTooltip';
import AppIcon from './AppIcon';
import { fallbackDocType } from '../../../util/DocUtils';
import { SxProps } from '@mui/material';

export const fallbackIcon = {
    path: 'M13.2 9.6H17.6L13.2 5.2V9.6ZM7.6 4H14L18.8 8.8V18.4C18.8 19.28 18.08 20 17.2 20H7.6C6.712 20 6 19.28 6 18.4V5.6C6 4.712 6.712 4 7.6 4ZM14.8 16.8V15.2H7.6V16.8H14.8ZM17.2 13.6V12H7.6V13.6H17.2Z',
    color: '#000000',
};

/**
 * Component that shows the DocType icon for a document in a project database.
 */
export default ({
    color,
    type,
    size,
    tooltip,
    tooltipPlacement,
    testMode,
    withLabel,
    labelSx = {},
}: IDocTypeIconProps) => {
    /** Configure component translation. */
    const { i18n } = useTranslation();

    /** Get relevant values from Redux store. */
    const docTypes = useSelector((store: IAppReduxStore) => store.projectDocTypes.data);

    /** Try to find a matching DocType configuration from the Redux store. */
    const match =
        docTypes.find(x => x.name === type) ?? docTypes.find(x => x.name === fallbackDocType);

    /** Determine icon color. */
    const fillColor = color === 'inherit' ? match?.color ?? vars.font : color;

    /** Detemine icon path. */
    const path = match?.icon ?? fallbackIcon.path;

    /** Determine the tooltip text, if needed. */
    const tooltipText = tooltip || tooltipPlacement ? match?.translations[i18n.language] : null;

    return (
        <AppIcon
            {...{
                path,
                color: fillColor,
                size,
                tooltipText,
                tooltipPlacement,
                testMode,
                withLabel,
                labelSx,
                label: match?.translations[i18n.language] ?? match.name,
            }}
        />
    );
};

/**
 * Interface that describes the props for a DocTypeIcons component.
 */
export interface IDocTypeIconProps {
    /** Icon type. */
    type: string;

    /** (Optional) Whether to show a tooltip or not. */
    tooltip?: boolean;

    /** (Optional) Icon Tooltip Placement. */
    tooltipPlacement?: TooltipPlacement;

    /** (Optional) Icon color. */
    color?: string | 'inherit' | 'currentColor';

    /** (Optional) Icon size. */
    size?: number;

    /** (Optional) TestMode property. */
    testMode?: boolean;

    /** (Optional) Defines Typography component. */
    withLabel?: boolean;

    /** (Optional) Defines Typography's extra styles. */
    labelSx?: SxProps;
}
